import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

export const AuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>, 
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  // Inject services
  const authService = inject(AuthService);
  const router = inject(Router);
  
  // Skip intercepting for certain endpoints (like login, public routes, static assets)
  // Skip intercepting for certain endpoints (like login, public routes)
  // Also skip OneSignal API - it uses Basic auth with REST API key, not Bearer JWT
  const skipInterceptorRoutes = [
    '/assets/',
    'assets/',
    '/login',
    '/register',
    'https://onesignal.com',
    '/sendOtp',
    '/verifyOtp',
    // Forgot password flows (company / financial advisor)
    '/forgot-password',
    '/auth/forgot-password',
    '/api/auth/forgot-password',
    '/company/auth/login',
    '/company/auth/sendOtp',
    '/company/auth/verifyOtp',
    '/public/',
    'https://api.countrystatecity.in/v1/' 
  ];

  const shouldSkipInterceptor = skipInterceptorRoutes.some(route => 
    req.url.includes(route)
  );

  if (shouldSkipInterceptor) {
    console.log('AuthInterceptor: Skipping interceptor for:', req.url);
    return next(req);
  }

  // Get the auth token and validate it
  const token = authService.getToken();

  console.log('AuthInterceptor: Token retrieved:', token ? 'Token exists' : 'No token found');

  // Check if user is authenticated (this includes token validation)
  if (!authService.isAuthenticated()) {
    console.log('AuthInterceptor: User not authenticated, redirecting to login');
    authService.logout();
    return throwError(() => new Error('Authentication required'));
  }

  // Add token to request if it exists and is valid
  if (token) {
    console.log('AuthInterceptor: Adding Authorization header to request');
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  // Pass the cloned request with token
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      console.error('AuthInterceptor: HTTP Error:', error.status, error.message);
      
      if (error.status === 401) {
        // Handle 401 Unauthorized errors - Token expired or invalid
        console.log('AuthInterceptor: 401 Unauthorized - Token expired or invalid');
        authService.logout();
        
        // Don't redirect if already on login page
        if (!router.url.includes('/login') && !router.url.includes('/otp-verification')) {
          router.navigate(['/login'], { 
            queryParams: { 
              returnUrl: router.url,
              message: 'Your session has expired. Please login again.'
            } 
          });
        }
        
        return throwError(() => new Error('Your session has expired. Please login again.'));
      } else if (error.status === 403) {
        // Handle 403 Forbidden errors - User doesn't have permission
        console.log('AuthInterceptor: 403 Forbidden - Access denied');
        
        // ✅ FIX: Skip redirect for chat initiation endpoint - let component handle it
        // This endpoint returns 403 with specific business logic (non-participant in existing chat)
        if (req.url.includes('/chats/initiate-for-project')) {
          console.log('AuthInterceptor: Skipping redirect for chat initiation - component will handle');
          // Return error as-is so component can handle it properly
          return throwError(() => error);
        }
        
        // Get user role and redirect to appropriate default route
        const userRole = authService.getUserRole();
        if (userRole) {
          console.log('AuthInterceptor: Redirecting to default route for role:', userRole);
          authService.redirectToDefaultRoute();
        } else {
          authService.logout();
          router.navigate(['/login']);
        }
        
        return throwError(() => new Error('You do not have permission to access this resource.'));
      } else if (error.status === 500) {
        // Handle server errors
        console.error('AuthInterceptor: Server error:', error);
        return throwError(() => new Error('Server error occurred. Please try again later.'));
      } else if (error.status === 0) {
        // Handle network errors
        console.error('AuthInterceptor: Network error:', error);
        return throwError(() => new Error('Could not connect to server. Please check your internet connection.'));
      } else if (error.status === 404) {
        // Preserve HttpErrorResponse so callers can branch on status (e.g. empty tax invoices list).
        console.error('AuthInterceptor: Resource not found:', error);
        return throwError(() => error);
      } else if (error.status === 408) {
        // Request timeout (e.g. from FileUploadTimeoutInterceptor)
        console.error('AuthInterceptor: Request timeout:', error);
        return throwError(() => error);
      }

      // Show user-friendly error messages for other common issues
      let errorMessage = 'An error occurred. Please try again.';
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
      
      // Return the error for further handling
      return throwError(() => new Error(errorMessage));
    })
  );
};